<script>
	import { browser, dev } from '$app/environment';
	import { onDestroy, onMount } from 'svelte';
	import { page } from '$app/stores';

	import Header from '$lib/header/Header.svelte';
	import Footer from '$lib/footer/Footer.svelte';

	import { generalStore } from '$stores/general';
	import { ticketTotal, usedDiscountCode, userCodeDiscount } from '$stores/cart';
	import { isOnline } from '$stores/connection';

	import {
		daysRemaining,
		handlePromiseRejection,
		injectReferrerCode,
		showToast,
		untilLiveDraw,
		untilThursdayDraw
	} from '$lib/helpers';
	import { getPrizeCount } from '$lib/firestore/getPrizeCount';
	import { recentWinners } from '$lib/firestore/recentWinners';

	import { runKlaviyo } from '$lib/klaviyo';

	import { inject } from '@vercel/analytics';
	import { injectSpeedInsights } from '@vercel/speed-insights/sveltekit';
	import { logError } from '$lib/helpers/errors';

	import '$src/app.css';
	import 'animate.css';
	import { auth, setupAuthListener } from '$lib/auth';
	import { get, writable } from 'svelte/store';
	import { userAuth } from '$stores/user';
	import { subscribeToPrizes } from '$stores/prizes';
	import { productStore, recentWinnersStore } from '$stores/products';
	import { referralCodeStore } from '$stores/referrals';
	import posthog, { PostHog } from 'posthog-js';
	import SubscriptionOfferModal from '$components/SubscriptionOfferModal.svelte';
	import { flags } from '$stores/flags';
	import { iOsOrAndroid, isUserFromApp } from '$lib/helpers/device';
	import { isAppUser, isAppUserType } from '$stores/isAppUser';
	import { afterNavigate } from '$app/navigation';
	import { onAuthStateChanged } from 'firebase/auth';
	
	import { BootstrapToast, ToastContainer } from 'svelte-toasts';
	import SeoComp from '$lib/SeoComp.svelte';
	// import DailyPromo from '$components/DailyPromo.svelte';

	injectSpeedInsights();
	inject({ mode: dev ? 'development' : 'production' });

	let previousTicketTotal;

	let daysUntilMonday;
	let daysUntilThursday;
	let prizeCount;
	let filters = [];

	const excludedPaths = ['/checkout', '/cart', '/account'];

	// Reactive statement for Klaviyo
	$: if (browser && $page?.url) runKlaviyo();

	// Referral codes
	$: referralCode = $page.url.searchParams.get('ref');

	$: currentPagHref = get(page).url.href;

	$: (async function () {
		if (!browser && !referralCode) return;

		const isExcluded = excludedPaths.some((path) => currentPagHref.includes(path));

		if ($referralCodeStore) {
			if (!isExcluded) {
				await injectReferrerCode();
			}
		}
	})();

	// Subscribe to ticketTotal and handle discounts
	const unsubscribeTicketTotal = ticketTotal.subscribe((value) => {
		if (browser) {
			if (previousTicketTotal !== undefined && value !== previousTicketTotal) {
				userCodeDiscount.set('');
				usedDiscountCode.set('');
			}
			previousTicketTotal = value;
		}
	});

	const unsubscribeGeneral = generalStore.subscribe(($generalStore) => {
		daysUntilMonday = $generalStore.daysUntilMonday;
		daysUntilThursday = $generalStore.daysUntilThursday;
		prizeCount = $generalStore.prizeCount;
		filters = $generalStore.filters;
	});

	onDestroy(() => {
		unsubscribeTicketTotal();

		unsubscribeGeneral();
	});

	async function fetchUniqueProducts() {
		let activeProds;

		activeProds = $productStore;
		const uniqSet = [...new Set(activeProds)];
		if (JSON.stringify($productStore) !== JSON.stringify(uniqSet)) {
			productStore.set(uniqSet);
		}
	}

	async function fetchRecentWinnersData() {
		recentWinnersStore.set(await recentWinners(10, false));
	}

	async function fetchData() {
		try {
			if (!browser) return;

			await Promise.all([fetchUniqueProducts(), fetchRecentWinnersData()]);
			runKlaviyo();
		} catch (error) {
			logError(error, 'Error in layout fetchData');
		}
	}


	const API_HOST = 'https://us-central1-staging-ac5f0.cloudfunctions.net/rewardDailyVisitHttp';

	/* NEW – uses your http wrapper ------------------ */
	async function rewardDailyVisit() {
		const user = auth.auth.currentUser;
		const token = user ? await user.getIdToken() : undefined;

		// `httpRequest` is assumed to return the parsed JSON `{ credited, amount, ... }`
		return fetch(`${API_HOST}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: token ? `Bearer ${token}` : ''
			},
			body: JSON.stringify({}) // adjust body as needed
		}).then((response) => {
			
			return response.json();
		}).then((d) => {
			if (d.credited) {
				showToast('Success', `You have been credited £${d.amount.toFixed(2)} for your daily visit!`);
			}
			
		});
	}

	onMount(() => {
		if (!browser) return;

		// console.log('flags: ', $flags);

		// Unregister service worker
		// if ('serviceWorker' in navigator) {
		// 	navigator.serviceWorker.getRegistrations().then((registrations) => {
		// 		for (const registration of registrations) {
		// 			registration.unregister().then((success) => {
		// 				console.log('Service worker unregistered:', success);
		// 			});
		// 		}
		// 	});
		// }

		subscribeToPrizes();

		setupAuthListener();

		if (!$page.url.pathname.includes('/checkout')) {
			fetchData();
		}

		window.addEventListener('unhandledrejection', handlePromiseRejection);

		// if (dev) {
			// console.log('store auth: ', $userAuth);
		// }

		// console.log('Layout script evaluated');

		if (referralCode) {
			// Store the referral code in localStorage
			localStorage.setItem('referralCode', referralCode);
			referralCodeStore.set(referralCode);

			// **Posthog Event: Referral Code Captured**
			posthog.capture('referral_code_captured', {
				referralCode
			});
		} else {
			// Check if referral code is already stored
			const storedCode = localStorage.getItem('referralCode');
			if (storedCode) {
				referralCodeStore.set(storedCode);
			}
		}

		// **Posthog Event: User from App**
		posthog.register({
			source: $isAppUser ? 'app' : 'web',
			appType: $isAppUserType ? $isAppUserType : 'web'
		});

		// isUserFromApp().then((isFromApp) => {
		// 	if (isFromApp) {
		// 	}
		// });

		// Wait until Firebase has hydrated the auth state
		const unsubscribe = onAuthStateChanged(auth.auth, (user) => {
			if (!user) return;
			// console.log('about to reward daily visit');
			// First call	 on load
			rewardDailyVisit()
				// .then(( data ) => {
				// 	console.log({data})
				// 	if (data?.credited) {
				// 		console.log(`🎉 Credited £${data.amount} today!`);
						
				// 	}
				// })
				.catch((err) => console.error('Promo error', err));

			// Then call on every client‑side nav
		});

		return () => {
			window.removeEventListener('unhandledrejection', handlePromiseRejection);
			unsubscribe();
		};
	});

	afterNavigate(() => {
		if (browser && auth?.auth?.currentUser) {
			rewardDailyVisit().catch((err) => console.error('Promo error', err));
		}
	});
</script>

<!-- <SeoComp /> -->



<Header />

{#if !$isOnline && browser}
	<div class="offline-notification" role="alert" aria-live="assertive">
		You are currently offline. Some features may not be available.
	</div>
{/if}

<SubscriptionOfferModal />

{#if $page.error}
	<div class="your-error-layout">
		<h1>An error occurred</h1>
		<p>{$page.error.message}</p>
	</div>
{:else}
	<div id="hdWrap">
		<slot />
	</div>
{/if}

<ToastContainer let:data>
	<BootstrapToast {data} />
</ToastContainer>

<Footer />

<style lang="scss" global>
	@keyframes shake {
		10%,
		90% {
			transform: translate3d(0, -1px, 0);
		}
		20%,
		80% {
			transform: translate3d(0, 2px, 0);
		}
		30%,
		50%,
		70% {
			transform: translate3d(0, -4px, 0);
		}
		40%,
		60% {
			transform: translate3d(0, 4px, 0);
		}
	}

	:global(.red-banner) {
		background-color: #fe1100;
		// background-color: #E66C2C; // halloween
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 0 20px;
		h4 {
			color: #fff;
			font-weight: 400;
		}
		h5 {
			color: #fff;
			font-weight: 400;
		}
		a {
			color: #fff;
			font-weight: 600;
			text-decoration: underline;
		}
	}
	:global(.red-banner.fixed) {
		position: sticky;
		top: 60px;
		left: 0;
		width: 100%;
		z-index: 9;

		@media only screen and (min-width: 869px) {
			top: 50px;
		}
	}

	.shakeitlikeits-hot {
		animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
		transform: translate3d(0, 0, 0);
		backface-visibility: hidden;
		perspective: 1000px;
	}

	#hdWrap {
		min-height: 70vh;
	}

	.list-flex {
		overflow-x: scroll;
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		justify-content: space-between;

		&::-webkit-scrollbar {
			height: 4px;
			border-radius: 4px;
		}
		&::-webkit-scrollbar-track {
			box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
			border-radius: 4px;
		}
		&::-webkit-scrollbar-thumb {
			background-color: darkgrey;
			border-radius: 4px;
		}
	}

	.no-format {
		list-style-type: none;
		padding: 0;
	}

	.g-bg {
		background-color: #f1f1f1;
	}

	form {
		input[type='submit'],
		button {
			max-width: 280px;
		}
	}

	.flex-row {
		flex-direction: row !important;
	}

	.flex-between {
		justify-content: space-between;
	}

	.text-right {
		text-align: right;
	}

	.btn-grey {
		background-color: #ccc;
		color: #222;
		min-height: 40px;

		&:hover,
		&:focus {
			background-color: #666;
			color: #f1f1f1;
		}
	}

	.svelte-lazy-image--loaded {
		border-radius: 10px 10px 0 0;
	}

	.offline-notification {
		background-color: rgb(254, 17, 0);
		color: #222;
		padding: 10px 20px;
	}
</style>
